import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GearIcon from '@rsuite/icons/Gear'

import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import { SmallStandardButton } from '../../../ui_reusable/styles/Button.styles'
import { TagSuccess, TagWarning } from '../../../../utils/tag'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { VerySmallNormalTag } from '../../../ui_reusable/styles/Tags.styles'
import { useClientContext } from '../../../../context/ClientContext'
import { useProjectContext } from '../../../../context/ProjectContext'

const PAGE_SIZE = 8

const ClientProjectList = ({ clientId }) => {
  const navigate = useNavigate()
  const { setSelectedClient } = useClientContext()
  const { setSelectedProject } = useProjectContext()
  const [projects, setProjects] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const abortController = new AbortController()

    const getClientProjects = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
          client: String(clientId),
        })
        if (search) {
          params.set('search', search)
        }
        if (sort) {
          params.set(
            'ordering',
            `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
          )
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages — step back and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const responseData = await response.json()

        setProjects(responseData?.data || [])
        setTotal(responseData?.total ?? responseData?.data?.length ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch projects:', error)
          setProjects([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    if (clientId) {
      getClientProjects()
    } else {
      setLoading(false)
      setProjects([])
    }

    return () => {
      abortController.abort()
    }
  }, [clientId, page, search, sort])

  const handleManage = useCallback(
    (projectId) => {
      if (!projectId) return

      const project = projects.find((elem) => elem.id === projectId)
      if (project) {
        setSelectedClient(project.client)
        setSelectedProject({
          id: project.id,
          name: project.name,
          logo: project.logo,
        })
      }
      navigate(`/projects/${projectId}`)
    },
    [navigate, projects, setSelectedClient, setSelectedProject],
  )

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(nextSort)
  }, [])

  const getFlattenedProjectData = () => {
    return projects.map((project) => ({
      ...project,
      client: project.client?.name || '',
      kam: project.kam?.name || '',
      solutionsSearchable:
        project.solutions?.map((s) => s.name).join(', ') || '',
    }))
  }

  const headers = [
    {
      title: 'Project name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
    },
    {
      title: 'Client',
      key: 'client',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Created',
      key: 'created_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      serverKey: 'created_at',
    },
    {
      title: 'Solutions',
      key: 'solutionsSearchable',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Status',
      key: 'status',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'KAM',
      key: 'kam',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: '',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
    },
  ]

  const renderRow = (rowData) => [
    <GridCellBoldText>{rowData.name}</GridCellBoldText>,
    <GridCellNormalText>
      {rowData.client?.name || rowData.client}
    </GridCellNormalText>,
    <GridCellSmallFadedText>
      {new Date(rowData.created_at).toISOString().slice(0, 10)}
    </GridCellSmallFadedText>,
    <GridCellNormalText>
      {Array.isArray(rowData.solutions) && rowData.solutions.length > 0 ? (
        rowData.solutions.map((solution, index) => (
          <VerySmallNormalTag key={index}>{solution.name}</VerySmallNormalTag>
        ))
      ) : (
        <GridCellSmallFadedText>No solutions</GridCellSmallFadedText>
      )}
    </GridCellNormalText>,
    <GridCellNormalText>
      {rowData.status === 'active' ? (
        <TagSuccess label={rowData.status} />
      ) : (
        <TagWarning label={rowData.status} />
      )}
    </GridCellNormalText>,
    <GridCellSmallFadedText>
      {rowData.kam?.name || rowData.kam || 'N/A'}
    </GridCellSmallFadedText>,
    <SmallStandardButton
      onClick={() => handleManage(rowData.id)}
      startIcon={<GearIcon />}
    >
      Manage
    </SmallStandardButton>,
  ]

  return (
    <VersatileGrid
      headers={headers}
      data={getFlattenedProjectData()}
      renderRow={renderRow}
      maxDisplayedItems={PAGE_SIZE}
      serverSide
      serverTotal={total}
      serverPage={page}
      onPageChange={handlePageChange}
      onSearchChange={handleSearchChange}
      onSortChange={handleSortChange}
      loading={loading}
      loadingMessage="Loading projects..."
    />
  )
}

export default ClientProjectList
